<template>
  <section class="section-block">
    <SectionTitle title="Line 綁定設定" hideBtn />

    <BobForm
      :formRef.sync="formRef"
      :fields="displayFields"
      :model="formData"
      label-position="left"
      label-width="300px"
    />
  </section>
</template>

<script>
import BobForm from '@/components/BobForm.vue'
import FormItemTooltip from '@/components/FormItem/FormItemTooltip.vue'
import { GetLineBindingConfig, UpdateLineBindingConfig } from '@/api/line'
import { useBaseForm } from '@/use/useForm'
import { useShop } from '@/use/shop'
import { usePermissions } from '@/use/permissions'
import { computed, defineComponent, onMounted, provide, watch } from 'vue'

export default defineComponent({
  name: 'LineBindingSetting',
  components: { BobForm },
  setup () {
    const { shopId } = useShop()
    const { checkAction } = usePermissions()
    const { formData, formRef, initFormData, loading } = useBaseForm()

    initFormData({
      enabledJoinFriend: false,
      requiredJoinFriend: false,
      bindLineVisibleMaskPhone: false,
      requiredLineBindingForEcommerce: false,
    })

    provide('formData', formData)

    const showMaskPhoneToggle = computed(() => checkAction('planModule.maskPhoneText.enable'))
    const showEcommerceToggle = computed(() => checkAction('planModule.ecommerce.enable'))

    const getLineBindingConfig = async ({ force = false } = {}) => {
      if (loading.value && !force) return

      loading.value = true
      let res
      let err

      try {
        [res, err] = await GetLineBindingConfig({ shopId: shopId.value })
      } catch (error) {
        err = error?.message || error || '取得設定失敗'
      } finally {
        loading.value = false
      }

      if (err) {
        window.$message.error(err)
        return
      }

      formData.enabledJoinFriend = res.enabledJoinFriend ?? false
      formData.requiredJoinFriend = res.requiredJoinFriend ?? false
      formData.bindLineVisibleMaskPhone = res.bindLineVisibleMaskPhone ?? false
      formData.requiredLineBindingForEcommerce = res.requiredLineBindingForEcommerce ?? false
    }

    const updateLineBindingConfig = async () => {
      if (loading.value) return

      loading.value = true
      let err

      const canSendEcommerce = showEcommerceToggle.value && formData.enabledJoinFriend

      try {
        [, err] = await UpdateLineBindingConfig({
          shopId: shopId.value,
          enabledJoinFriend: formData.enabledJoinFriend,
          requiredJoinFriend: formData.enabledJoinFriend && formData.requiredJoinFriend,
          bindLineVisibleMaskPhone: formData.bindLineVisibleMaskPhone,
          requiredLineBindingForEcommerce: canSendEcommerce ? formData.requiredLineBindingForEcommerce : false,
        })
      } catch (error) {
        err = error?.message || error || '更新失敗'
      } finally {
        loading.value = false
      }

      if (err) {
        window.$message.error(err)
        await getLineBindingConfig({ force: true })
        return
      }

      window.$message.success('更新成功！')
    }

    watch(() => formData.enabledJoinFriend, (enabledJoinFriend) => {
      if (!enabledJoinFriend) {
        formData.requiredJoinFriend = false
        formData.requiredLineBindingForEcommerce = false
      }
    })

    const displayFields = computed(() => {
      const omitProps = []
      if (!formData.enabledJoinFriend) omitProps.push('requiredJoinFriend')
      if (!showMaskPhoneToggle.value) omitProps.push('bindLineVisibleMaskPhone')
      if (!showEcommerceToggle.value || !formData.enabledJoinFriend) omitProps.push('requiredLineBindingForEcommerce')

      const fields = [
        {
          prop: 'enabledJoinFriend',
          label: '綁定與加入好友提醒',
          body: {
            component: 'BaseElSwitch',
            props: { activeText: '開啟', inactiveText: '關閉', disabled: loading.value },
            on: { change: updateLineBindingConfig },
          },
        },
        {
          prop: 'requiredJoinFriend',
          label: '限制Line綁定會員才可使用系統服務',
          body: {
            component: 'BaseElSwitch',
            props: { activeText: '開啟', inactiveText: '關閉', disabled: loading.value },
            on: { change: updateLineBindingConfig },
          },
        },
        {
          prop: 'requiredLineBindingForEcommerce',
          component: FormItemTooltip,
          props: {
            label: '限制 LINE 綁定才可使用電商服務',
            tooltip: '開啟前，請先確認已完成「Line Login Channel 設定」，並開啟「外部瀏覽器不中斷綁定通知」',
          },
          body: {
            component: 'BaseElSwitch',
            props: { activeText: '開啟', inactiveText: '關閉', disabled: loading.value },
            on: { change: updateLineBindingConfig },
          },
        },
        {
          prop: 'bindLineVisibleMaskPhone',
          label: 'Line 綁定通知套用電話隱碼',
          body: {
            component: 'BaseElSwitch',
            props: { activeText: '開啟', inactiveText: '關閉', disabled: loading.value },
            on: { change: updateLineBindingConfig },
          },
        },
      ]

      return fields.filter(f => !omitProps.includes(f.prop))
    })

    onMounted(getLineBindingConfig)

    return { formData, formRef, displayFields }
  },
})
</script>

<style lang="postcss" scoped>

</style>
